import { useMemo } from 'react';

import type { CatalogueSearchSchema } from '@/core/components/CatalogueSearch/CatalogueSearchProvider';
import CatalogueDomain, { CountDomain } from '@/core/lib/new-architecture/domain/catalogue.domain';
import Query from '@/core/lib/new-architecture/query';
import Store from '@/core/lib/new-architecture/store';
import type { InfiniteStoreData, StoreData } from '@/core/lib/new-architecture/store/store.types';
import { type DonationsPublicDonationQuery, donationsPublicDonationQuerySchema } from '@/core/types/donation';

export const useThematique = (thematiqueId: string, queryParams?: DonationsPublicDonationQuery, enabled?: boolean): InfiniteStoreData<CatalogueDomain> => {
  const { data: current, isLoading: userIsLoading } = Store.current.useCurrent();
  const { data: categories, isLoading: categoriesIsLoading } = Store.categories.useCategories();
  const { data: favorites, isLoading: favoritesIsLoading } = Store.favorites.useFavorites();

  const query = donationsPublicDonationQuerySchema.cast({ ...queryParams, tags: [thematiqueId] }, { stripUnknown: true, assert: false });

  const { data: pages, isLoading: donationsIsLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch: refetchDonations } = Query.donations.useDonations({ data: query }, enabled);

  const { data: loc } = Query.locs.useDetail({ data: { search: query.loc } });

  const isLoading = userIsLoading || categoriesIsLoading || donationsIsLoading || favoritesIsLoading;

  const data = useMemo(() => {
    if (!pages || !categories) {
      return null;
    }

    return new CatalogueDomain({ data: pages, categories, current, query, loc, followedDonationsIds: favorites?.getFollowedDonationsIds() || [] });
  }, [pages, categories, current, queryParams, query, loc, favorites]);

  return {
    data,
    isLoading,
    isFetchingNextPage,
    notFound: !data,
    hasNext: hasNextPage,
    total: pages?.pages[0].total ?? 0,
    next: () => fetchNextPage(),
    refetchDonations,
  };
};

export const useCountThematique = (search: Partial<CatalogueSearchSchema> | null): StoreData<CountDomain> => {
  const { data: count, isLoading } = Query.donations.useDonationsCount({
    data: {
      cat: search?.cat?.alias,
      loc: search?.loc?.loc,
      distance: search?.loc?.distance,
      title: search?.title ?? undefined,
      tag: search?.tag,
    },
    enabled: !!search,
  });

  const data = useMemo(() => {
    if (!count) return null;

    return new CountDomain(count.total);
  }, [count]);

  return {
    data,
    isLoading,
    notFound: false,
  };
};
